import React, { useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Alert,
    Divider,
    Link,
} from '@mui/material';
import { Email, Phone, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Btn from '../global/Btn';
import FormInput from '../inputs/FormInput';
import OtpInput from './OtpInput';
import OtpCountdownTimer from './OtpCountdownTimer';
import OtpResendButton from './OtpResendButton';
import { useOtpRequest } from '../../hooks/auth/useOtpRequest';
import { useOtpVerify } from '../../hooks/auth/useOtpVerify';
import { useOtpResend } from '../../hooks/auth/useOtpResend';
import { useAuthContext } from '../../auth/useAuthContext';
import { maskEmail, maskPhoneNumber } from '../../utils/otpHelpers';
import { countries } from '../../constants/countres';

const FormOtpLogin = ({ onBackToPassword }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { loginWithOtp } = useAuthContext();

    const [step, setStep] = useState(1); // 1: Identifier, 2: Verification
    const [channel, setChannel] = useState('sms');
    const [email, setEmail] = useState('');
    const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].phone);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');

    const identifier = channel === 'sms' ? `+${selectedCountryCode}${phoneNumber}` : email;

    const otpRequest = useOtpRequest();
    const otpVerify = useOtpVerify();
    const otpResend = useOtpResend();

    const handleRequestOtp = () => {
        if (channel === 'email' && !email) {
            toast.error(t('validation.emailRequired'));
            return;
        }
        if (channel === 'sms' && !phoneNumber) {
            toast.error(t('validation.phoneRequired'));
            return;
        }

        otpRequest.mutate(
            { channel, identifier, purpose: 'login' },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_sent_success'));
                    setStep(2);
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.send_failed');
                    toast.error(message);
                }
            }
        );
    };

    const handleVerifyOtp = (otpCode) => {
        const cart_session_id = localStorage.getItem('cart_session_id');

        otpVerify.mutate(
            { channel, identifier, purpose: 'login', otp: otpCode, cart_session_id },
            {
                onSuccess: async (data) => {
                    if (data?.success && data?.data) {
                        if (loginWithOtp) {
                            await loginWithOtp(data.data);
                        }
                        toast.success(data?.message || t('otp.login_success'));
                        navigate('/');
                    }
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.invalid_code');
                    toast.error(message);
                }
            }
        );
    };

    const handleResend = () => {
        otpResend.mutate(
            { channel, identifier, purpose: 'login' },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_resent'));
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.resend_failed');
                    toast.error(message);
                }
            }
        );
    };

    const handleBack = () => {
        if (step === 2) {
            setStep(1);
            setOtp('');
        } else if (onBackToPassword) {
            onBackToPassword();
        }
    };

    const handleExpire = useCallback(() => {
        // Only show expired toast if we are not currently resending
        if (!otpResend.isPending) {
            toast.error(t('otp.code_expired'));
        }
    }, [otpResend.isPending, t]);

    return (
        <Paper
            elevation={4}
            sx={{
                px: { md: 4, xs: 2 },
                py: { md: 4, xs: 3 },
                borderRadius: 3,
                maxWidth: 420,
                mx: 'auto',
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="h5" textAlign="center" sx={{ mb: { md: 3, xs: 2 }, fontWeight: 600 }}>
                {step === 1 ? t('otp.login_title') : t('otp.verification')}
            </Typography>

            {step === 1 ? (
                <Box>
                    <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                        {t('otp.choose_method')}
                    </Typography>

                    <ToggleButtonGroup
                        value={channel}
                        exclusive
                        onChange={(e, value) => value && setChannel(value)}
                        fullWidth
                        sx={{
                            mb: 3,
                            '& .MuiToggleButton-root': {
                                py: 1.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                }
                            }
                        }}
                    >
                        <ToggleButton value="sms">
                            <Phone sx={{ mr: 1 }} />
                            {t('otp.sms')}
                        </ToggleButton>
                        <ToggleButton value="email">
                            <Email sx={{ mr: 1 }} />
                            {t('otp.email')}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {channel === 'sms' ? (
                            <FormInput
                                type="tel"
                                label={t('auth.phone')}
                                placeholder="555-555-555"
                                selectedCountryCode={selectedCountryCode}
                                setSelectedCountryCode={setSelectedCountryCode}
                                phoneNumber={phoneNumber}
                                setPhoneNumber={setPhoneNumber}
                                disabled={otpRequest.isPending}
                            />
                        ) : (
                            <FormInput
                                type="email"
                                label={t('auth.email')}
                                placeholder="example@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={otpRequest.isPending}
                            />
                        )}
                    </Box>

                    <Box mt={4} textAlign="center">
                        <Btn
                            type="button"
                            onClick={handleRequestOtp}
                            width="100%"
                            widthMob="100%"
                            sx={{ mx: 'auto' }}
                            loading={otpRequest.isPending}
                            disabled={!otpRequest.isPending && (channel === 'sms' ? !phoneNumber : !email)}
                            endIcon={<ArrowForward />}
                        >
                            {t('otp.send_code')}
                        </Btn>

                        <Divider sx={{ my: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('auth.orContinueWith')}
                            </Typography>
                        </Divider>

                        <Btn
                            type="button"
                            variant="outlined"
                            width="100%"
                            widthMob="100%"
                            onClick={handleBack}
                            disabled={otpRequest.isPending}
                        >
                            {t('auth.signIn')}
                        </Btn>
                    </Box>
                </Box>
            ) : (
                <Box>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        {t('otp.code_sent_to')}:<br />
                        <strong>
                            {channel === 'sms'
                                ? maskPhoneNumber(identifier)
                                : maskEmail(identifier)
                            }
                        </strong>
                    </Alert>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" mb={1} textAlign="center">
                            {t('otp.enter_code')}
                        </Typography>
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                            onComplete={handleVerifyOtp}
                            error={!!otpVerify.error}
                            disabled={otpVerify.isPending}
                        />
                    </Box>

                    {otpRequest.expiresAt && (
                        <Box sx={{ mb: 3 }}>
                            <OtpCountdownTimer
                                expiresAt={otpRequest.expiresAt}
                                onExpire={handleExpire}
                            />
                        </Box>
                    )}

                    {otpVerify.attemptsRemaining !== null && (
                        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                            {t('otp.attempts_remaining')}: {otpVerify.attemptsRemaining}
                        </Alert>
                    )}

                    <Btn
                        type="button"
                        onClick={() => handleVerifyOtp(otp)}
                        width="100%"
                        widthMob="100%"
                        loading={otpVerify.isPending}
                        disabled={!otpVerify.isPending && otp.length !== 6}
                        sx={{ mb: 2, mx: 'auto' }}
                    >
                        {t('otp.verify_login')}
                    </Btn>

                    <Divider sx={{ my: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.orContinueWith')}
                        </Typography>
                    </Divider>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <OtpResendButton
                            onResend={handleResend}
                            cooldownSeconds={otpResend.cooldownSeconds || otpRequest.cooldownSeconds}
                            loading={otpResend.isPending}
                        />

                        <Btn
                            type="button"
                            onClick={handleBack}
                            variant="text"
                            width="100%"
                            widthMob="100%"
                        >
                            {step === 2 ? t('otp.change_details') : t('common.previous')}
                        </Btn>
                    </Box>
                </Box>
            )}

            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2">
                    {t('auth.dontHaveAccount')}{' '}
                    <Link
                        component={RouterLink}
                        to="/register"
                        sx={{ fontWeight: 600, textDecoration: 'none' }}
                    >
                        {t('auth.signUp')}
                    </Link>
                </Typography>
            </Box>
        </Paper>
    );
};

export default FormOtpLogin;
